/**
 * KollectCare AI - API Service
 * Centralized API client with interceptors
 */

import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8787';

const api = axios.create({
  baseURL: `${API_URL}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor - add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle token expiration
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      // Try to refresh token
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, { token });
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed, logout user
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
          return Promise.reject(refreshError);
        }
      }
    }

    // Format error message
    const message = error.response?.data?.error || error.message || 'An error occurred';
    return Promise.reject({ ...error, message });
  }
);

// ============================================================================
// AUTH API
// ============================================================================

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  refresh: () => api.post('/auth/refresh'),
  me: () => api.get('/auth/me'),
};

// ============================================================================
// CONTACTS API
// ============================================================================

export const contactsAPI = {
  list: (params = {}) => api.get('/contacts', { params }),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  import: (formData) => api.post('/contacts/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  export: () => api.get('/contacts/export/csv', { responseType: 'blob' }),
};

// ============================================================================
// TAGS API
// ============================================================================

export const tagsAPI = {
  list: () => api.get('/tags'),
  create: (data) => api.post('/tags', data),
  delete: (id) => api.delete(`/tags/${id}`),
};

// ============================================================================
// CAMPAIGNS API
// ============================================================================

export const campaignsAPI = {
  list: (params = {}) => api.get('/campaigns', { params }),
  get: (id) => api.get(`/campaigns/${id}`),
  create: (data) => api.post('/campaigns', data),
  update: (id, data) => api.put(`/campaigns/${id}`, data),
  delete: (id) => api.delete(`/campaigns/${id}`),
  send: (id) => api.post(`/campaigns/${id}/send`),
};

// ============================================================================
// MESSAGES API
// ============================================================================

export const messagesAPI = {
  list: (params = {}) => api.get('/messages', { params }),
  send: (data) => api.post('/messages/send', data),
  getConversations: (params = {}) => api.get('/conversations', { params }),
  getConversationMessages: (id, params = {}) => api.get(`/conversations/${id}/messages`, { params }),
};

// ============================================================================
// TEMPLATES API
// ============================================================================

export const templatesAPI = {
  list: (params = {}) => api.get('/templates', { params }),
  get: (id) => api.get(`/templates/${id}`),
  create: (data) => api.post('/templates', data),
  update: (id, data) => api.put(`/templates/${id}`, data),
  delete: (id) => api.delete(`/templates/${id}`),
};

// ============================================================================
// WORKFLOWS API
// ============================================================================

export const workflowsAPI = {
  getAll: () => api.get('/workflows'),
  list: () => api.get('/workflows'),
  get: (id) => api.get(`/workflows/${id}`),
  create: (data) => api.post('/workflows', data),
  update: (id, data) => api.put(`/workflows/${id}`, data),
  delete: (id) => api.delete(`/workflows/${id}`),
  activate: (id) => api.post(`/workflows/${id}/activate`),
  deactivate: (id) => api.post(`/workflows/${id}/deactivate`),
  getExecutions: (id, params = {}) => api.get(`/workflows/${id}/executions`, { params }),
};

// ============================================================================
// ANALYTICS API
// ============================================================================

export const analyticsAPI = {
  dashboard: () => api.get('/analytics/dashboard'),
  campaigns: () => api.get('/analytics/campaigns'),
  contacts: () => api.get('/analytics/contacts'),
};

// ============================================================================
// WEBHOOKS API
// ============================================================================

export const webhooksAPI = {
  list: () => api.get('/webhooks'),
  create: (data) => api.post('/webhooks', data),
  update: (id, data) => api.put(`/webhooks/${id}`, data),
  delete: (id) => api.delete(`/webhooks/${id}`),
  test: (id) => api.post(`/webhooks/${id}/test`),
};

// ============================================================================
// SEGMENTS API
// ============================================================================

export const segmentsAPI = {
  list: (params = {}) => api.get('/segments', { params }),
  get: (id) => api.get(`/segments/${id}`),
  create: (data) => api.post('/segments', data),
  update: (id, data) => api.put(`/segments/${id}`, data),
  delete: (id) => api.delete(`/segments/${id}`),
  preview: (conditions) => api.post('/segments/preview', { conditions }),
  getContacts: (id, params = {}) => api.get(`/segments/${id}/contacts`, { params }),
};

// ============================================================================
// BROADCASTS API
// ============================================================================

export const broadcastsAPI = {
  list: (params = {}) => api.get('/broadcasts', { params }),
  get: (id) => api.get(`/broadcasts/${id}`),
  create: (data) => api.post('/broadcasts', data),
  update: (id, data) => api.put(`/broadcasts/${id}`, data),
  delete: (id) => api.delete(`/broadcasts/${id}`),
  send: (id) => api.post(`/broadcasts/${id}/send`),
  schedule: (id, data) => api.post(`/broadcasts/${id}/schedule`, data),
  cancel: (id) => api.post(`/broadcasts/${id}/cancel`),
  getStats: (id) => api.get(`/broadcasts/${id}/stats`),
};

// ============================================================================
// REPORTS API
// ============================================================================

export const reportsAPI = {
  list: () => api.get('/reports'),
  get: (type, params = {}) => api.get(`/reports/${type}`, { params }),
  export: (type, format = 'csv') => api.get(`/reports/${type}/export`, { 
    params: { format },
    responseType: 'blob' 
  }),
  getScheduled: () => api.get('/reports/scheduled'),
  createSchedule: (data) => api.post('/reports/scheduled', data),
  deleteSchedule: (id) => api.delete(`/reports/scheduled/${id}`),
};

// ============================================================================
// INTEGRATIONS API
// ============================================================================

export const integrationsAPI = {
  list: () => api.get('/integrations'),
  connect: (integrationId, data) => api.post(`/integrations/${integrationId}/connect`, data),
  disconnect: (integrationId) => api.post(`/integrations/${integrationId}/disconnect`),
  configure: (integrationId, data) => api.put(`/integrations/${integrationId}/config`, data),
  getStatus: (integrationId) => api.get(`/integrations/${integrationId}/status`),
};

// ============================================================================
// INBOX API (Conversations)
// ============================================================================

export const inboxAPI = {
  getConversations: (params = {}) => api.get('/inbox/conversations', { params }),
  getConversation: (id) => api.get(`/inbox/conversations/${id}`),
  getMessages: (conversationId, params = {}) => api.get(`/inbox/conversations/${conversationId}/messages`, { params }),
  sendMessage: (conversationId, data) => api.post(`/inbox/conversations/${conversationId}/messages`, data),
  markAsRead: (conversationId) => api.post(`/inbox/conversations/${conversationId}/read`),
  assignAgent: (conversationId, agentId) => api.post(`/inbox/conversations/${conversationId}/assign`, { agentId }),
  addNote: (conversationId, note) => api.post(`/inbox/conversations/${conversationId}/notes`, { note }),
  getQuickReplies: () => api.get('/inbox/quick-replies'),
  createQuickReply: (data) => api.post('/inbox/quick-replies', data),
};

// ============================================================================
// SETTINGS API
// ============================================================================

export const settingsAPI = {
  getWorkspace: () => api.get('/settings/workspace'),
  updateWorkspace: (data) => api.put('/settings/workspace', data),
  getTeam: () => api.get('/settings/team'),
  inviteTeamMember: (data) => api.post('/settings/team/invite', data),
};

export default api;
